import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { fmtDate } from "./shared";
import { CallTailDialog } from "./callTail";

const TERMINAL_CALL_STATUSES = new Set(["completed", "busy", "no-answer", "canceled", "failed"]);

/** Compact strip on the campaign detail page showing the last N Twilio calls. */
export function RecentTwilioCallsStrip({ limit = 5 }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tailSid, setTailSid] = useState(null);
  const [refreshing, setRefreshing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/outbound/twilio/calls?limit=${limit}`);
      setCalls(data.calls || []);
    } catch { /* ignore transient */ }
    finally { setLoading(false); }
  };

  const refreshRow = async (sid) => {
    setRefreshing(sid);
    try {
      const { data } = await api.post(`/outbound/twilio/calls/${sid}/refresh`);
      setCalls((prev) => prev.map((c) => (c.call_sid === sid ? data : c)));
      toast.success(`Status refreshed: ${data.status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not refresh call");
    } finally { setRefreshing(null); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const closeTail = () => { setTailSid(null); load(); };

  const statusPill = (s) => {
    const base = "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize";
    if (s === "completed") return `${base} border-emerald-200 bg-emerald-50 text-emerald-700`;
    if (["busy", "no-answer", "canceled", "failed"].includes(s)) return `${base} border-red-200 bg-red-50 text-red-700`;
    return `${base} border-blue-200 bg-blue-50 text-blue-700`;
  };

  return (
    <Card className="border border-slate-200 p-0 overflow-hidden" data-testid="ai-outbound-recent-calls-strip">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <div className="text-sm font-semibold text-slate-800">Recent calls</div>
        <button onClick={load} className="text-[11px] text-slate-400 hover:text-slate-700"
                data-testid="ai-outbound-recent-calls-refresh">Refresh</button>
      </div>
      {loading ? (
        <div className="px-4 py-6 text-center text-xs text-slate-400">Loading…</div>
      ) : calls.length === 0 ? (
        <div className="px-4 py-6 text-center text-xs text-slate-400" data-testid="ai-outbound-recent-calls-empty">
          No calls yet. Click <span className="font-medium text-slate-600">Test call</span> above to place one.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5 text-left">SID</th>
              <th className="px-3 py-1.5 text-left">To</th>
              <th className="px-3 py-1.5 text-left">Status</th>
              <th className="px-3 py-1.5 text-right">Duration</th>
              <th className="px-3 py-1.5 text-left">Placed</th>
              <th className="px-3 py-1.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {calls.map((r) => {
              const isTerminal = TERMINAL_CALL_STATUSES.has((r.status || "").toLowerCase());
              return (
                <tr key={r.call_sid} className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setTailSid(r.call_sid)}
                    data-testid={`ai-outbound-recent-call-row-${r.call_sid}`}>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-slate-600">{r.call_sid.slice(0, 10)}…</td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{r.to || "—"}</td>
                  <td className="px-3 py-1.5">
                    <span className={statusPill(r.status)}>
                      {!isTerminal && <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" />}
                      {(r.status || "").replace(/-/g, " ")}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right text-xs">{r.duration_sec != null ? `${r.duration_sec}s` : "—"}</td>
                  <td className="px-3 py-1.5 text-[11px] text-slate-500">{r.created_at ? fmtDate(r.created_at) : ""}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button className="mr-3 text-xs text-primary hover:underline"
                            onClick={(e) => { e.stopPropagation(); setTailSid(r.call_sid); }}
                            data-testid={`ai-outbound-recent-call-tail-${r.call_sid}`}>Watch</button>
                    {!isTerminal && (
                      <button className="text-xs text-slate-500 hover:text-slate-800 hover:underline"
                              onClick={(e) => { e.stopPropagation(); refreshRow(r.call_sid); }}
                              disabled={refreshing === r.call_sid}
                              data-testid={`ai-outbound-recent-call-refresh-${r.call_sid}`}>
                        {refreshing === r.call_sid ? "…" : "Refresh"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <CallTailDialog sid={tailSid} open={!!tailSid} onClose={closeTail} />
    </Card>
  );
}
